import { AfterViewInit, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ImageRotationStorageService } from '../../services/image-rotation-storage.service';

@Component({
    selector: 'shared-image-viewer',
    templateUrl: './shared-image-viewer.component.html',
    styleUrls: ['./shared-image-viewer.component.scss'],
    standalone: false
})
export class SharedImageViewerComponent implements AfterViewInit, OnChanges {
  private readonly minScale = 0.2;
  private readonly maxScale = 4;
  private readonly scaleStep = 0.1;

  @Input() imageUrl: string = '';
  @Input() examParseId: unknown;
  @ViewChild('imageElement') imageElement!: ElementRef<HTMLImageElement>;
  @ViewChild('imageContainer') imageContainer!: ElementRef<HTMLDivElement>;

  private rotation = 0;
  private scale = 1;

  constructor(
    private imageRotationStorageService: ImageRotationStorageService,
  ) {}

  get transform() {
    return `rotate(${this.rotation}deg) scale(${this.scale})`;
  }

  ngAfterViewInit() {
    this.loadSavedRotation();
    this.adjustImageSize();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.imageUrl || changes.examParseId) {
      this.loadSavedRotation();
      this.adjustImageSize();
    }
  }

  rotate() {
    this.rotation = (this.rotation + 90) % 360;
    this.imageRotationStorageService.setRotation(this.examParseId, this.rotation);
    this.adjustImageSize();
  }

  zoomIn() {
    this.scale = this.roundScale(Math.min(this.maxScale, this.scale + this.scaleStep));
    this.adjustImageSize();
  }

  zoomOut() {
    this.scale = this.roundScale(Math.max(this.minScale, this.scale - this.scaleStep));
    this.adjustImageSize();
  }

  reset() {
    this.rotation = 0;
    this.scale = 1;
    this.imageRotationStorageService.resetRotation(this.examParseId);
    this.imageElement.nativeElement.style.transform = this.transform;
    this.imageElement.nativeElement.style.width = '';
    this.imageElement.nativeElement.style.height = '';
    this.adjustImageSize();
  }

  adjustImageSize() {
    if (!this.imageElement || !this.imageContainer) {
      return;
    }
    const image = this.imageElement.nativeElement;
    const container = this.imageContainer.nativeElement;
    image.style.transform = this.transform;

    const imageNaturalWidth = image.naturalWidth;
    const imageNaturalHeight = image.naturalHeight;

    if (this.rotation % 180 !== 0) {
      // Swap width and height
      container.style.width = `${imageNaturalHeight * this.scale}px`;
      container.style.height = `${imageNaturalWidth * this.scale}px`;
    } else {
      container.style.width = `${imageNaturalWidth * this.scale}px`;
      container.style.height = `${imageNaturalHeight * this.scale}px`;
    }
  }

  private loadSavedRotation(): void {
    this.rotation = this.imageRotationStorageService.getRotation(this.examParseId);
  }

  private roundScale(scale: number): number {
    return Math.round(scale * 10) / 10;
  }
}
